import React, { useCallback, useMemo } from "react";
import { MenuItem } from "@blueprintjs/core";
import { Form } from "react-final-form";
import Button, { ButtonSize, ButtonStyle } from "components/Button";
import Input from "rs-components/Input";
import SearchSelectInput from "rs-components/Input/components/SearchSelectInput";
import ParameterValueAppearance from "../ParameterValueAppearance";
import { OptimizationParameterParameterType, OptimizationTargetType, } from "store/rs-workspace/rs-workspace.types";
import { setupAlternativeStepOptimizationParameter } from "store/rs-workspace/utils/scenario.utils";
import { getSelectedParameterToOptimize } from "store/rs-workspace/utils/common.utils";
import { getCellModelParametersToOptimizeList } from "store/cell-model/cell-model.utils";
import { isNotEmpty } from "utils/validate";
import { DIALOG_ACTION_TITLE } from "shared/labels";
import { getRecipeParametersToOptimizeList } from "store/recipe/recipe.utils";
import * as multiSelectStyles from "components/Controls/MultiSelect/styles.module.scss";
import * as styles from "./styles.module.scss";
var Fields;
(function (Fields) {
    Fields["parameterId"] = "parameterId";
    Fields["stepId"] = "stepId";
})(Fields || (Fields = {}));
const isSubmitDisabled = (values) => {
    const field = values.type === OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE
        ? values[Fields.parameterId]
        : values[Fields.stepId];
    return !isNotEmpty(field);
};
const handleSelectParameter = (form, value) => {
    const fieldName = value.type === OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE
        ? Fields.parameterId
        : Fields.stepId;
    form.restart({ type: value.type, [fieldName]: value.id });
};
const SelectParameterForm = ({ renderProps, index, target, targetType, parameterOverrides, usedVariantStepFlows, restrictions, }) => {
    const { fields } = renderProps;
    const usedParameters = useMemo(() => fields.value.filter((_, i) => index !== i), [fields.value, index]);
    const parameterItems = useMemo(() => {
        if (!isNotEmpty(target?.state?.parameters)) {
            return [];
        }
        return targetType === OptimizationTargetType.RECIPE
            ? getRecipeParametersToOptimizeList(target?.state, usedParameters)
            : getCellModelParametersToOptimizeList(target?.state?.parameters, usedParameters.map((optimizationParameter) => optimizationParameter.type === OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE
                ? `${optimizationParameter.parameterId}`
                : undefined));
    }, [target?.state, targetType, usedParameters]);
    const onSubmit = useCallback((values) => {
        let valuesToUpdate;
        if (values.type === OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE) {
            valuesToUpdate = {
                parameterId: values.parameterId,
            };
        }
        else {
            const stepId = values.stepId;
            const usedFlowId = usedVariantStepFlows
                ? usedVariantStepFlows.find(({ stepId }) => stepId === values.stepId)?.flowId
                : 1;
            const flowIds = isNotEmpty(usedFlowId) ? [Number(usedFlowId)] : [];
            valuesToUpdate = setupAlternativeStepOptimizationParameter({
                stepId,
                flowIds,
                type: values.type || OptimizationParameterParameterType.ALTERNATIVE_FLOW,
            }, target);
        }
        fields.update(index, {
            type: values.type,
            ...valuesToUpdate,
            notSaved: false,
            id: (fields?.length ?? 0) + 1,
        });
    }, [fields, index, target, usedVariantStepFlows]);
    return (React.createElement(Form, { onSubmit: onSubmit, validateOnBlur: true, render: ({ handleSubmit, values, form }) => {
            const selectedParameter = getSelectedParameterToOptimize(values, target, targetType, parameterOverrides);
            return (React.createElement(React.Fragment, null,
                React.createElement(Input, { name: values.type === OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE
                        ? Fields.parameterId
                        : Fields.stepId, component: SearchSelectInput, items: parameterItems, noResultsRenderer: React.createElement(MenuItem, { className: multiSelectStyles.emptyList, disabled: true, text: "No matching parameters." }), errorLabel: "Parameter", restrictions: restrictions, classes: { component: styles["parameter-name"] }, disableFormChange: true, onChange: (value) => handleSelectParameter(form, value), value: selectedParameter?.name }),
                React.createElement(ParameterValueAppearance, { parameter: selectedParameter, usedVariantStepFlows: usedVariantStepFlows, stepId: values?.[Fields.stepId] }),
                React.createElement("div", { className: styles["parameter-buttons"] },
                    React.createElement(Button, { onClick: () => fields.pop(), classes: { root: styles["parameter-buttons-button"] }, label: DIALOG_ACTION_TITLE.CANCEL, role: "button", style: ButtonStyle.Secondary, size: ButtonSize.Normal, showTooltipIfDisabled: true }),
                    React.createElement(Button, { classes: { root: styles["parameter-buttons-button"] }, label: DIALOG_ACTION_TITLE.OK, role: "button", onClick: handleSubmit, style: ButtonStyle.Primary, size: ButtonSize.Normal, showTooltipIfDisabled: true, disabled: isSubmitDisabled(values) }))));
        } }));
};
export default SelectParameterForm;
